import type { AuthenticatedUser } from '../auth/model.js';
import type { MspHealthPrototypeModel } from '../model.js';
import type { OnboardingRequestDetail, OnboardingRequestListItem } from '../../onboarding/request.js';
import { escapeAttribute, escapeHtml } from '../html.js';
import { renderAuthenticatedShell } from '../view.js';
import {
  devicePlanLabel, emailFailureLabel, emailStatusLabel, emailTypeLabel,
  formatOnboardingDate, formatOnboardingTimestamp, requestStatusLabel, taskSourceLabel,
} from './presentation.js';

function field(label: string, value: string | null): string {
  return value?.trim() ? `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>` : '';
}

function statusBadge(label: string, tone: 'pending' | 'sent' | 'failed' | 'neutral'): string {
  return `<span class="onboarding-status" data-tone="${tone}">${escapeHtml(label)}</span>`;
}

function requestList(requests: readonly OnboardingRequestListItem[] | undefined, feedback: string): string {
  return `<section class="content-section onboarding-operations" aria-label="Onboarding requests">
    <div class="section-title"><h2>Requests</h2><span>${requests?.length ?? 0} current</span></div>
    ${feedback ? `<p role="status">${escapeHtml(feedback)}</p>` : ''}
    ${requests === undefined ? '<p>Loading requests…</p>'
    : requests.length === 0 ? '<p>No onboarding requests have been submitted.</p>'
    : `<div class="onboarding-table-scroll"><table class="onboarding-request-table">
      <thead><tr><th scope="col">Request</th><th scope="col">Client / Employee</th><th scope="col">Start date</th><th scope="col">Requester</th><th scope="col">Status</th><th scope="col">Created</th></tr></thead>
      <tbody>${requests.map((request) => `<tr>
        <td><a class="onboarding-request-link" href="/onboarding/${escapeAttribute(request.requestNumber)}" data-app-route>${escapeHtml(request.requestNumber)}</a></td>
        <td><strong>${escapeHtml(request.clientName)}</strong><small>${escapeHtml(request.employeeName)}</small></td>
        <td>${escapeHtml(formatOnboardingDate(request.employeeStartDate))}</td>
        <td>${escapeHtml(request.requesterName)}</td>
        <td>${statusBadge(requestStatusLabel(request.status), 'pending')}</td>
        <td>${escapeHtml(formatOnboardingTimestamp(request.createdAt))}</td>
      </tr>`).join('')}</tbody></table></div>`}
  </section>`;
}

const RESOURCE_GROUPS = [
  ['Access', 'access'], ['Hardware', 'hardware'], ['Software', 'software'],
  ['Shared drives', 'shared-drives'], ['Groups', 'groups'],
  ['Custom access', 'custom-access'], ['Custom hardware', 'custom-hardware'],
  ['Other', 'other'],
] as const;

function resourceGroup(kind: string, code: string): string {
  if (code.startsWith('custom-access:')) return 'custom-access';
  if (code.startsWith('custom-hardware:')) return 'custom-hardware';
  return {
    ACCESS: 'access', HARDWARE: 'hardware', SOFTWARE: 'software',
    SHARED_DRIVE: 'shared-drives', GROUP: 'groups',
  }[kind] ?? 'other';
}

function resources(detail: OnboardingRequestDetail): string {
  const rows = RESOURCE_GROUPS.map(([title, key]) => {
    const selections = detail.selections.filter((item) => resourceGroup(item.kind, item.code) === key);
    return selections.length === 0 ? '' : `<div class="onboarding-resource-group"><h4>${title}</h4><div class="onboarding-chip-list">${selections.map((item) =>
      `<span class="onboarding-chip">${escapeHtml(item.label)}</span>`).join('')}</div></div>`;
  }).join('');
  return `<section class="onboarding-detail-section"><h3>Requested resources</h3>${rows || '<p class="onboarding-empty">No resources requested.</p>'}</section>`;
}

function checklist(detail: OnboardingRequestDetail): string {
  return `<section class="onboarding-detail-section"><h3>Generated checklist</h3>
    ${detail.tasks.length === 0 ? '<p class="onboarding-empty">No checklist items.</p>'
    : `<ol class="onboarding-checklist">${detail.tasks.map((task) => `<li>
      <div><strong>${escapeHtml(task.title)}</strong>${task.notes ? `<p>${escapeHtml(task.notes)}</p>` : ''}</div>
      <span class="onboarding-source">${escapeHtml(taskSourceLabel(task.source))}</span>
    </li>`).join('')}</ol>`}
  </section>`;
}

function emailDelivery(detail: OnboardingRequestDetail): string {
  return `<section class="onboarding-detail-section"><h3>Email delivery</h3>
    ${detail.emailDeliveries.length === 0 ? '<p class="onboarding-empty">No delivery records.</p>'
    : `<div class="onboarding-delivery-list">${detail.emailDeliveries.map((item) => {
      const tone = item.status === 'SENT' ? 'sent' : item.status === 'FAILED' ? 'failed'
        : item.status === 'PENDING' || item.status === 'IN_PROGRESS' ? 'pending' : 'neutral';
      return `<div class="onboarding-delivery-row"><div class="onboarding-delivery-heading"><strong>${escapeHtml(emailTypeLabel(item.emailType))}</strong>${statusBadge(emailStatusLabel(item.status), tone)}</div>
        ${item.recipient ? `<p>Recipient: ${escapeHtml(item.recipient)}</p>` : ''}
        ${item.lastError ? `<p>Reason: ${escapeHtml(emailFailureLabel(item.lastError))}</p>
          <details class="onboarding-technical"><summary>Technical detail</summary><code>${escapeHtml(item.lastError)}</code></details>` : ''}
      </div>`;
    }).join('')}</div>`}
  </section>`;
}

function requestDetail(detail: OnboardingRequestDetail): string {
  return `<div class="onboarding-operations onboarding-detail">
    <a class="onboarding-back-link" href="/onboarding" data-app-route>← All requests</a>
    <header class="onboarding-detail-header"><div class="onboarding-detail-title"><h2>${escapeHtml(detail.requestNumber)}</h2>${statusBadge(requestStatusLabel(detail.status), 'pending')}</div>
      <dl class="onboarding-summary">
        ${field('Client', detail.clientName)}${field('Employee', detail.employeeName)}
        ${field('Start date', formatOnboardingDate(detail.employeeStartDate))}${field('Requester', detail.requesterName)}
      </dl>
    </header>
    <section class="onboarding-detail-section"><h3>Employee &amp; request</h3><dl class="onboarding-detail-grid">
      ${field('Employee', detail.employeeName)}${field('Job title', detail.jobTitle)}
      ${field('Location', detail.locationName)}${field('Department', detail.departmentName)}
      ${field('Profile', detail.profileName ?? detail.customRoleText)}
      ${field('Requester', detail.requesterName)}${field('Requester email', detail.requesterEmail)}
      ${field('Future employee email', detail.futureEmployeeEmail)}
      ${field('Start date', formatOnboardingDate(detail.employeeStartDate))}
      ${field('Special access notes', detail.specialAccessNotes)}${field('Request details', detail.requestDetails)}
    </dl></section>
    <section class="onboarding-detail-section"><h3>Device plan</h3><dl class="onboarding-detail-grid">
      ${field('Plan', devicePlanLabel(detail.devicePlan))}
      ${field('Computer username', detail.computerUsername)}
      ${field('Computer / asset tag', detail.computerNameOrAssetTag)}
      ${field('Previous computer user', detail.previousComputerUser)}
    </dl></section>
    ${resources(detail)}${checklist(detail)}${emailDelivery(detail)}
  </div>`;
}

export function renderOnboardingOperations(
  dashboard: MspHealthPrototypeModel,
  user: AuthenticatedUser,
  requests: readonly OnboardingRequestListItem[] | undefined,
  detail: OnboardingRequestDetail | null,
  feedback: string,
): string {
  return renderAuthenticatedShell(user, dashboard.managedClientCount, 'ONBOARDING', `
    <main class="workspace" data-view="onboarding">
      <header class="page-heading"><h1>Onboarding</h1><p>Submitted employee requests awaiting review</p></header>
      ${detail === null ? requestList(requests, feedback) : requestDetail(detail)}
    </main>`);
}
